import React, { useCallback, useEffect, useRef } from 'react';
import { Node, mergeAttributes, ReactNodeViewRenderer } from '@tiptap/react';
import { NodeViewWrapper, NodeViewProps } from '@tiptap/react';
import 'mathlive';
import 'mathlive/static.css';

/** Inline atomic node; LaTeX persists in `data-latex`. Do NOT add MathLive to StarterKit globally — ~250KB. */

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'math-field': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          value?: string;
          placeholder?: string;
          readOnly?: boolean;
          'virtual-keyboard-mode'?: 'manual' | 'onfocus' | 'off';
          'read-only'?: boolean;
        },
        HTMLElement
      >;
    }
  }
}

interface MathAttrs {
  latex: string;
  /** When true, expand to full MathLive editor; when false, show KaTeX-rendered preview. */
  editing: boolean;
}

const MathNodeView: React.FC<NodeViewProps> = ({ node, updateAttributes, editor }) => {
  const fieldRef = useRef<HTMLElement | null>(null);
  const latex = (node.attrs as MathAttrs).latex || '';
  const editing = !!(node.attrs as MathAttrs).editing;

  // Sync MathLive value when external updates happen
  useEffect(() => {
    if (!fieldRef.current) return;
    const el = fieldRef.current as any;
    if (el.value !== latex) el.value = latex;
  }, [latex]);

  // Wire MathLive input event back into TipTap
  const onInput = useCallback(
    (e: Event) => {
      const next = (e.target as any).value || '';
      if (next !== latex) updateAttributes({ latex: next });
    },
    [latex, updateAttributes],
  );

  useEffect(() => {
    const el = fieldRef.current as any;
    if (!el) return;
    el.addEventListener('input', onInput);
    return () => el.removeEventListener('input', onInput);
  }, [onInput]);

  // Defensive teardown: hide MathLive's global virtual keyboard before the
  // <math-field> is removed. Otherwise MathLive's _Menu dismiss handler runs
  // against a stale element and throws "Cannot read properties of undefined
  // (reading 'classList')". This mirrors the same guard in MathFieldInput.
  useEffect(() => {
    return () => {
      try {
        const kb = (window as unknown as { mathVirtualKeyboard?: { hide?: () => void } })
          .mathVirtualKeyboard;
        kb?.hide?.();
      } catch {
        /* swallow — already on unmount path */
      }
    };
  }, []);

  const exitEdit = () => updateAttributes({ editing: false });
  const enterEdit = () => {
    if (!editor.isEditable) return;
    updateAttributes({ editing: true });
  };

  if (editing) {
    return (
      <NodeViewWrapper
        as="span"
        className="inline-flex items-center align-middle border border-foreground/30 rounded px-1 mx-0.5 bg-muted/40"
      >
        <math-field
          ref={fieldRef as React.RefObject<HTMLElement>}
          virtual-keyboard-mode="onfocus"
          onBlur={exitEdit}
        >
          {latex}
        </math-field>
      </NodeViewWrapper>
    );
  }

  // Read-only preview — KaTeX is applied by RenderedHtml in document mode,
  // but inside the editor we render a plain span and rely on a small inline
  // helper so authors see the rendered equation. KaTeX is imported lazily.
  return (
    <NodeViewWrapper
      as="span"
      className="math-tex inline-block align-middle cursor-pointer rounded px-1 hover:bg-muted"
      data-latex={latex}
      onDoubleClick={enterEdit}
      onClick={enterEdit}
    >
      <KatexInline latex={latex} />
    </NodeViewWrapper>
  );
};

// Tiny KaTeX wrapper, lazy-loaded so we don't pull katex into the toolbar bundle.
const KatexInline: React.FC<{ latex: string }> = ({ latex }) => {
  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    import('katex').then((katex) => {
      if (cancelled || !ref.current) return;
      try {
        katex.default.render(latex, ref.current, {
          throwOnError: false,
          displayMode: false,
        });
      } catch {
        if (ref.current) ref.current.textContent = `\\(${latex}\\)`;
      }
    });
    return () => {
      cancelled = true;
    };
  }, [latex]);

  return <span ref={ref} />;
};

export const MathExtension = Node.create({
  name: 'math',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      latex: { default: '' },
      editing: { default: false, rendered: false },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span.math-tex',
        getAttrs: (node) => {
          const el = node as HTMLElement;
          const latex = el.getAttribute('data-latex') || el.textContent || '';
          return { latex: latex.replace(/^\\\(|\\\)$/g, '') };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes, node }) {
    const latex = (node.attrs as MathAttrs).latex || '';
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        class: 'math-tex',
        'data-latex': latex,
      }),
      `\\(${latex}\\)`,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(MathNodeView);
  },

  addCommands() {
    return {
      insertMath:
        (latex: string = '') =>
        ({ chain }: any) =>
          chain()
            .focus()
            .insertContent({ type: this.name, attrs: { latex, editing: !latex } })
            .run(),
    } as any;
  },
});

/** TipTap v3's `Commands` interface isn't augmentable from app code — use `chainWithMath(editor)`. */

export default MathExtension;
